import { Check, Info, OctagonAlert, TriangleAlert, X } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useLocalStorage } from 'usehooks-ts'

export function Banner ({
  titleText,
  children,
  type = 'default',
  className = '',
  button,
  dismissableLocalStorageKey,
  onDismiss = () => {},
  overrideIcon,
}: {
  titleText: React.ReactNode | string
  children?: React.ReactNode | string
  button?: React.ReactNode
  type?: string
  className?: string
  dismissableLocalStorageKey?: string,
  onDismiss?: () => void,
  overrideIcon?: React.ReactNode
}) {
  const [isDisplayed, setIsDisplayed] = useLocalStorage(
    dismissableLocalStorageKey || '',
    true
  )

  const [surfaceClass, setSurfaceClass] = useState(
    'bg-ds-bg-surfaces-default border-ds-border-primary-default'
  )
  const [titleClass, setTitleClass] = useState('text-ds-text-default')
  const [iconClass, setIconClass] = useState('')
  const [IconComponent, setIconComponent] =
    useState<React.ForwardRefExoticComponent<any>>(Info)

  useEffect(() => {
    switch (type) {
      case 'success':
        setSurfaceClass(
          'bg-ds-bg-success-default border-ds-border-info-success'
        )
        setTitleClass('text-ds-text-info-success-default')
        setIconClass('text-ds-icon-success')
        setIconComponent(Check)
        break
      case 'attention':
        setSurfaceClass(
          'bg-ds-bg-info-caution-default border-ds-border-info-caution'
        )
        setTitleClass('text-ds-text-info-caution-default')
        setIconClass('text-ds-icon-caution')
        setIconComponent(Info)
        break
      case 'warning':
        setSurfaceClass(
          'bg-ds-bg-info-warning-default border-ds-border-info-warning'
        )
        setTitleClass('text-ds-text-info-warning-default')
        setIconClass('text-ds-icon-warning')
        setIconComponent(TriangleAlert)
        break
      case 'critical':
        setSurfaceClass(
          'bg-ds-bg-info-critical-default border-ds-border-info-critical'
        )
        setTitleClass('text-ds-text-info-critical-default')
        setIconClass('text-ds-icon-critical')
        setIconComponent(OctagonAlert)
        break
      case 'info':
        setSurfaceClass('bg-ds-bg-info-default border-ds-border-info-default')
        setTitleClass('text-ds-text-info-default')
        setIconClass('text-ds-icon-info')
        setIconComponent(Info)
        break
      default:
        setSurfaceClass(
          'bg-ds-bg-surfaces-default border-ds-border-primary-default'
        )
        setTitleClass('text-ds-text-default')
        setIconClass('')
        setIconComponent(Info)
    }
  }, [type])

  if (!isDisplayed) return null

  return (
    <div
      data-testid='banner-container'
      className={`flex ${className} text-sm rounded-lg border p-4 ${surfaceClass} relative`}
    >
      {dismissableLocalStorageKey && (
        <div
          data-testid='banner-dismiss'
          className='absolute flex items-center cursor-pointer'
          style={{ top: '4px', right: '4px' }}
          onClick={() => {
            setIsDisplayed(false)
            onDismiss()
          }}
        >
          <X size={12} />
        </div>
      )}
      <div>
        <p className={`flex font-medium items-center ${titleClass} ${children ? 'mb-4' : ''}`}>
          {overrideIcon || <IconComponent size='16' className={`mr-2 ${iconClass}`} />}
          {titleText}
        </p>
        <div className='text-ds-text-default'>
          {typeof children === 'string'
            ? (
              <p className='text-sm'>{children}</p>
              )
            : (
                children
              )}
        </div>
      </div>
      {button && (
        <div data-testid='banner-button' className='flex items-center'>
          {button}
        </div>
      )}
    </div>
  )
}

/*
 * Shows up to three errors by default,
 * and the rest of the errors under "show more" expandable area.
 */
export function ErrorListBanner ({
  titleText,
  genericErrorMessage,
  errors,
}: {
  titleText: string
  genericErrorMessage: string
  errors: string[]
}) {
  const [showMore, setShowMore] = useState(false)

  let errorsToShow = errors
  let moreErrors: string[] = []
  if (errors.length > 3) {
    errorsToShow = errors.slice(0, 3)
    moreErrors = errors.slice(3)
  }
  return (
    <Banner type='critical' titleText={titleText}>
      <p className='mb-4'>{genericErrorMessage}</p>
      {errorsToShow.length > 0 && (
        <ul>
          {errorsToShow.map((error, index) => {
            return <li key={`error-${index}`}>{error}</li>
          })}
        </ul>
      )}
      {moreErrors.length > 0 && (
        <a
          onClick={() => setShowMore(!showMore)}
          className='cursor-pointer mt-2 btn-link'
          tabIndex={0}
        >
          {showMore ? 'See less...' : 'See more...'}
        </a>
      )}
      {moreErrors.length > 0 && showMore && (
        <ul>
          {moreErrors.map((error, index) => {
            return <li key={`error-${index}`}>{error}</li>
          })}
        </ul>
      )}
    </Banner>
  )
}
